/**
 * `/studio/money` — `mockups/studio/money.html`, whole.
 *
 * Seven sections, each in its own file, each drawn from one of three reads:
 *
 * | § | Section | Read |
 * |---|---|---|
 * | the answer | `AnswerRegion` | `analytics_net_cash` + `analytics_receivables` + the queue |
 * | the run | `RunSection` | `billing_summary.run` / `.runs` / `.invoice_ledger` |
 * | money out | `MoneyOutSection` | `analytics_net_cash` |
 * | net cash | `NetCashSection` | `analytics_net_cash` |
 * | failed payments | `FailedSection` | `billing_summary.failed_payments` / `.ageing` |
 * | wise queue | `WiseSection` | `payments_list_unmatched`, falling back to the summary |
 * | six months | `SixMonthsSection` | `billing_summary.recognised_vs_collected` |
 *
 * **The page computes nothing.** Every figure is a field; where the mockup prints a
 * number that no field carries — the run total, the six-month gap column, two
 * percentages in the opening sentence, the per-client past-due rollup — the section
 * that would have printed it says so in its own header and the session report lists
 * it. `ReviewQueueItem` is the one exception and it is arithmetic on an *input*: the
 * allocation of a credit, which `payments_match$` refuses unless it adds up.
 *
 * The URL is the state: `?period=` is the topbar's seg, and a past month is a real
 * address, which is why the route owns the search schema.
 */

import {
  PeriodSchema,
  firstDayOf,
  lastDayOf,
  parsePeriod,
  periodOf,
  type Money,
} from '@fareast/shared'
import { z } from 'zod'

import { useStudioRead } from '../../../api/studio.js'
import { PeriodSeg } from '../../../components/client/index.js'
import { Empty } from '../../../components/portal/Disclosure.js'
import { now } from '../../../integrations/clock.js'
import { useSearchState } from '../../../search/useSearchState.js'
import { PageTitle } from '../../../shell/PageTitle.js'
import { AnswerRegion } from './AnswerRegion.js'
import { DryRunDialog } from './DryRunDialog.js'
import { FailedSection } from './FailedSection.js'
import { MoneyOutSection } from './MoneyOutSection.js'
import { NetCashSection } from './NetCashSection.js'
import { ReconcileButton } from './ReconcileButton.js'
import { RunSection } from './RunSection.js'
import { SixMonthsSection } from './SixMonthsSection.js'
import { WiseSection } from './WiseSection.js'
import { useClientBook } from './names.js'

export const MoneySearchSchema = z.object({
  period: PeriodSchema.optional(),
})

export function MoneyPage() {
  const [search, setSearch] = useSearchState(MoneySearchSchema)
  const latest = periodOf(now())
  const period = search.period ?? latest

  const summary = useStudioRead('billing_summary', { period })
  const cash = useStudioRead('analytics_net_cash', { period })
  /*
   * Two reads that are not per-period and are rendered inline rather than at the
   * route boundary. Receivables is "what is past due *now*", which is not a property
   * of the month being looked at; the unmatched queue is the same, and it is read
   * separately from `billing_summary.wise_queue` so that placing a credit refetches
   * the list rather than the whole page.
   */
  const receivables = useStudioRead('analytics_receivables', {}, { inline: true })
  const unmatched = useStudioRead('payments_list_unmatched', {}, { inline: true })
  const { nameOf, clients } = useClientBook()

  const title = (
    <PageTitle
      eyebrow="Studio"
      title="Money"
      sub="The run, what is owed, what went out, and the credits nobody has placed"
      actions={
        <>
          <PeriodSeg
            value={period}
            latest={latest}
            months={2}
            onChange={(next) => {
              setSearch({ period: parsePeriod(next) })
            }}
          />
          <DryRunDialog period={period} clients={clients} nameOf={nameOf} />
          <ReconcileButton />
        </>
      }
    />
  )

  if (summary.isPending || cash.isPending) {
    return (
      <>
        {title}
        <Empty>Reading the run, the ledger and every payment that left the account…</Empty>
      </>
    )
  }

  if (summary.data === undefined || cash.data === undefined) {
    return (
      <>
        {title}
        <Empty>We could not read this month&rsquo;s money. Try again in a moment.</Empty>
      </>
    )
  }

  const money = summary.data
  const out = cash.data
  /* The live list when it has answered, the summary's own copy until then. */
  const queue = unmatched.data?.payments ?? money.wise_queue

  return (
    <>
      {title}

      <AnswerRegion
        period={period}
        out={out}
        queue={queue}
        receivables={receivables.data ?? null}
        months={money.recognised_vs_collected}
        nameOf={nameOf}
      />

      <RunSection
        period={period}
        run={money.run}
        runs={money.runs}
        ledger={money.invoice_ledger}
        clients={clients}
        nameOf={nameOf}
      />

      <MoneyOutSection out={out} />

      <NetCashSection
        out={out}
        firstDay={firstDayOf(parsePeriod(period))}
        lastDay={lastDayOf(parsePeriod(period))}
        queue={queue}
      />

      <FailedSection
        failed={money.failed_payments}
        ageing={money.ageing}
        pastDue={pastDueOf(receivables.data)}
        nameOf={nameOf}
      />

      <WiseSection queue={queue} nameOf={nameOf} />

      <SixMonthsSection months={money.recognised_vs_collected} />
    </>
  )
}

/**
 * What is still owed on an invoice, and for how long — by reference.
 *
 * `payment_failed_reminder` takes `days_past_due` and an `amount` its own schema
 * describes as "**what is outstanding**", and `FailedPaymentSchema` carries neither:
 * it has the invoice's `total`, which is a different number the moment any of it has
 * been settled. `analytics_receivables` is the only read that has both, so the
 * reminder dialog is handed its own invoice's pair from here.
 *
 * A reference the receivables read does not know about is not past due at all — that
 * is a recovered row, and it gets no reminder button.
 */
export type PastDue = { readonly days: number; readonly outstanding: Money }

export function pastDueOf(
  receivables:
    | {
        readonly invoices: readonly {
          readonly ref: string
          readonly days_past_due: number
          readonly outstanding: Money
        }[]
      }
    | undefined,
): ReadonlyMap<string, PastDue> {
  return new Map(
    (receivables?.invoices ?? []).map((one) => [
      one.ref,
      { days: one.days_past_due, outstanding: one.outstanding },
    ]),
  )
}
